/**
 * ManagementDashboardEntryScreen.js
 *
 * The Manager "Dashboard" tab. Decides, per account, between the two dashboards:
 *
 *  - workspaces OFF (default, and every existing user): the unchanged classic
 *    {@link module:screens/ManagementDashboardScreen}, with one extra header
 *    button that turns the workspace dashboard on;
 *  - workspaces ON (a `mobileWorkspaces` entry exists on the account / local
 *    mirror): {@link module:screens/WorkspaceDashboardScreen}, whose first
 *    workspace IS the classic dashboard (the migration of the old layout).
 *
 * This is the least disruptive integration: nobody's dashboard changes until
 * they opt in, the opt-in is one tap, and turning it off again deletes only the
 * `mobileWorkspaces` namespace (the classic layout was never touched).
 *
 * @module screens/ManagementDashboardEntryScreen
 */

import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import haptics from '../helpers/haptics';
import {useWorkspaces} from '../helpers/workspaces/useWorkspaces';
import ManagementDashboardScreen from './ManagementDashboardScreen';
import WorkspaceDashboardScreen from './WorkspaceDashboardScreen';

/**
 * @returns {JSX.Element} The classic or the workspace dashboard.
 */
export default function ManagementDashboardEntryScreen() {
    const {t} = useTranslation();
    const ws = useWorkspaces();
    const enable = useCallback(() => {
        haptics.success();
        ws.enable({name: t('wsd_overview_name')});
    }, [ws, t]);

    // Never block the classic dashboard on the account read: it renders at once and the tab
    // switches over only if the account turns out to have workspaces enabled.
    if (!ws.enabled) {
        return <ManagementDashboardScreen onOpenWorkspaces={enable} />;
    }

    return <WorkspaceDashboardScreen />;
}
